import { matchPerson } from '@weavix/domain/src/people/match-person';
import { PermissionApiResponse, PermissionAction, PermissionApiResponseItem, SITE_SETTINGS_FALLBACK } from '@weavix/models/src/permission/permissions.model';
import { PersonFacilityPredicate, PersonLike } from '@weavix/models/src/person/person-matcher';
import { memoize } from 'lodash';

/**
 * WARNING: this class has a similar implementation in @weavix/domain/src/people/permission-checker.ts
 */
export class PermissionChecker {

    constructor(private readonly permissions: PermissionApiResponse) {}

    /**
     * Returns `true` if there is:
     * * an account permission
     * * facility specified and a facility permission matching
     * * folder specified and a folder permission matching
     */
    public hasPermission(action: PermissionAction, facilityId?: string, folderId?: string): boolean {
        return this.permissions.grants
            .filter(perm => this.isPermissionInScope(perm, facilityId, folderId))
            .some(perm => this.isPermissionGranted(perm, action));
    }

    // returns true if there is:
    // * an account permission
    // * a facility permission matching
    public hasPermissionInAnyFacility(action: PermissionAction): boolean {
        return this.permissions.grants
            .filter(perm => this.isPermissionInAnyFacilityScope(perm))
            .some(perm => this.isPermissionGranted(perm, action));
    }

    /**
     * Returns true if there is:
     * * an account permission
     * * no facility specified and any facility permission
     * * facility specified and a matching facility permission
     * * no folder specified and any folder premission
     * * folder specified and a matching folder permission
     */
    public hasAnyPermission(action: PermissionAction, facilityId?: string, folderId?: string): boolean {
        return this.permissions.grants
            .filter(perm => !facilityId || perm.scope.facilityId === facilityId)
            .filter(perm => !folderId || !perm.scope.folderIds?.length || perm.scope.folderIds?.includes(folderId))
            .some(perm => this.isPermissionGranted(perm, action));
    }

    // returns true if there is:
    // * an account permission
    // * facility specified and a matching facility permission
    // * no folder specified and any folder permission
    // * folder specified and a matching folder permission
    public hasFacilityOrAnyFolderPermission(action: PermissionAction, facilityId?: string, folderId?: string): boolean {
        return this.permissions.grants
            .filter(perm => !perm.scope.facilityId || perm.scope.facilityId === facilityId)
            .filter(perm => !folderId || !perm.scope.folderIds?.length || perm.scope.folderIds?.includes(folderId))
            .some(perm => this.isPermissionGranted(perm, action));
    }

    addFolder(id: string, parentId: string) {
        this.permissions.grants.forEach(x => {
            const parent = x.scope.folderIds?.indexOf(parentId);
            if (parent >= 0) {
                x.scope.folderIds.push(id);
            }
        });
    }

    private isPermissionInScope(permission: PermissionApiResponseItem, facilityId?: string, folderId?: string): boolean {
        if (permission.scope.facilityId && facilityId !== permission.scope.facilityId) {
            return false;
        }
        if (permission.scope.folderIds?.length && !permission.scope.folderIds.includes(folderId)) {
            return false;
        }
        return true;
    }

    private isPermissionInAnyFacilityScope(permission: PermissionApiResponseItem): boolean {
        return !permission.scope.folderIds?.length;
    }

    private isPermissionGranted(permission: PermissionApiResponseItem, action: PermissionAction): boolean {
        return permission.grantedActions.some(grantedAction => this.isActionGranted(action, grantedAction));
    }

    /**
     * Whether `grantedAction` covers `desiredAction`: the account must be entitled to it or its site-settings fallback,
     * and the grant must be it, its fallback, or Administer Account. Entitlement and grant are judged separately, as on the server.
     */
    private isActionGranted(desiredAction: PermissionAction, grantedAction: PermissionAction): boolean {
        const fallback = SITE_SETTINGS_FALLBACK[desiredAction];
        const enabled = this.permissions.enabledActions;
        if (!enabled.includes(desiredAction) && !(fallback && enabled.includes(fallback))) return false;
        return grantedAction === PermissionAction.AdministerAccount || grantedAction === desiredAction || (!!fallback && grantedAction === fallback);
    }

    public isGlobalAdmin() {
        return this.permissions.grants.some(grant => grant.groupId === 'global-admin' && !grant.scope.facilityId);
    }

    hasAccountPermission(action: PermissionAction) {
        return this.permissions.enabledActions.includes(action);
    }

    hasPersonPermission = memoize((action: PermissionAction, person: PersonLike, isPersonOnFacility?: PersonFacilityPredicate, facilityId?: string) => {
        return this.permissions.grants.some(perm => {
            if (facilityId && perm.scope.facilityId && perm.scope.facilityId !== facilityId) return false;
            if (!perm.grantedActions.includes(action)
                && !perm.grantedActions.includes(PermissionAction.AdministerAccount)) return false;
            return action === PermissionAction.ViewDirectory && (!perm.directory || matchPerson(perm.directory, person, isPersonOnFacility, this.permissions.self))
                || action === PermissionAction.ViewWorkerLiveLook && (!perm.livelook || matchPerson(perm.livelook, person, isPersonOnFacility, this.permissions.self));
        });
    }, (action, person, _, facilityId) => `${action}:${person.id}:${facilityId}`);
}
